import { useState, useEffect, useRef } from 'react'

type SpinnerDelayOptions = {
  minDuration?: number
  delay?: number
}

type State = 'IDLE' | 'DELAY' | 'DISPLAY' | 'EXPIRE'

const defaultOptions: SpinnerDelayOptions = {
  minDuration: 300,
  delay: 500,
}

/**
 * Delay when to exactly show the Skeleton / Spinner, and when showed at minimum for how long.
 *
 * @important Be aware while your api is loading, this delay is only meant for showing the loading state
 * visually. So when to actually show there are "no results" you need to know about the data response of
 * the api.
 */
export function useSpinnerDelay(loading: boolean, options: SpinnerDelayOptions = defaultOptions): boolean {
  const [state, setState] = useState<State>('IDLE')

  const timeout = useRef<NodeJS.Timeout | undefined>(undefined)

  useEffect(() => {
    if (loading && state === 'IDLE') {
      clearTimeout(timeout.current)

      timeout.current = setTimeout(() => {
        timeout.current = setTimeout(() => {
          setState('EXPIRE')
        }, options.minDuration)

        setState('DISPLAY')
      }, options.delay)
    }

    if (!loading && state !== 'DISPLAY') {
      clearTimeout(timeout.current)

      setState('IDLE')
    }

    return () => {}
  }, [loading, state, options.delay, options.minDuration])

  useEffect(() => () => clearTimeout(timeout.current), [])

  return state === 'DISPLAY' || state === 'EXPIRE'
}
